import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";


const APPROVAL_ROLES = new Set([
  "super_admin",
  "operations_admin",
]);


const CREATE_ROLES = new Set([
  "super_admin",
  "operations_admin",
]);


function money(value) {
  const n = Number(value || 0);

  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(n);
}


function number(value, digits = 2) {
  const n = Number(value || 0);

  return n.toLocaleString("en-AU", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}


function roleOf(currentUser) {
  return String(
    currentUser?.role ||
    currentUser?.user_role ||
    ""
  ).toLowerCase();
}


function actorId(currentUser) {
  return String(
    currentUser?._id ||
    currentUser?.id ||
    currentUser?.user_id ||
    currentUser?.email ||
    ""
  );
}


function sameValue(a, b) {
  return Number(a || 0) === Number(b || 0);
}


function fieldLabel(field) {
  const labels = {
    hours: "Hours",
    rate: "Hourly Rate",
    amount: "Pay Amount",
  };

  return labels[field] || field;
}


function statusLabel(status) {
  if (!status) return "Unknown";

  return (
    status.charAt(0).toUpperCase() +
    status.slice(1)
  );
}


function badgeClass(status) {
  switch (status) {
    case "approved":
    case "finalised":
    case "exported":
      return "rx-badge rx-badge-approved";

    case "pending":
    case "reopened":
      return "rx-badge rx-badge-pending";

    case "rejected":
      return "rx-badge rx-badge-rejected";

    case "voided":
      return "rx-badge rx-badge-voided";

    default:
      return "rx-badge";
  }
}


export default function PayrollAdjustmentsPanel({
  api,
  start,
  end,
  engagementType = "all",
  state = "NSW",
  currentUser = null,
}) {
  const [preview, setPreview] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");

  // V4.2C-2 — Finalise / Lock / Export state
  const [batchState, setBatchState] = useState(null);
  const [batchWorking, setBatchWorking] = useState(false);
  const [showFinalise, setShowFinalise] = useState(false);
  const [finaliseNote, setFinaliseNote] = useState("");
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  // V4.2C-5 — Operational Payroll Close
  const [showClosePeriod, setShowClosePeriod] =
    useState(false);

  const [closeNote, setCloseNote] =
    useState("");

  // V4.2C-3 — Revision History UI
  const [showRevisionHistory, setShowRevisionHistory] =
    useState(false);

  const [revisionHistory, setRevisionHistory] =
    useState([]);

  const [revisionHistoryLoading, setRevisionHistoryLoading] =
    useState(false);

  const [selectedLine, setSelectedLine] =
    useState(null);

  const [createForm, setCreateForm] = useState({
    field: "hours",
    beforeValue: "",
    afterValue: "",
    reason: "",
  });

  const [decision, setDecision] = useState(null);

  const [decisionReason, setDecisionReason] =
    useState("");


  const role = roleOf(currentUser);

  const canCreate =
    !role ||
    CREATE_ROLES.has(role);

  const canApprove =
    !!role &&
    APPROVAL_ROLES.has(role);


  const loadData = useCallback(async () => {
    if (!start || !end) return;

    setLoading(true);
    setError("");

    try {
      const previewQuery = new URLSearchParams({
        start,
        end,
        engagement_type: engagementType,
        state,
      });

      const adjustmentQuery = new URLSearchParams({
        period_start: start,
        period_end: end,
      });

      const batchQuery = new URLSearchParams({
        start,
        end,
        engagement_type: engagementType,
        state,
      });

      const [
        previewResponse,
        adjustmentResponse,
        batchResponse,
      ] = await Promise.all([
        api.get(
          `/payroll/unified/preview?${previewQuery.toString()}`
        ),

        api.get(
          `/payroll/adjustments?${adjustmentQuery.toString()}`
        ),

        api.get(
          `/payroll/batches/status?${batchQuery.toString()}`
        ),
      ]);

      const previewPayload =
        previewResponse.data;

      const adjustmentPayload =
        adjustmentResponse.data;

      const batchPayload =
        batchResponse.data;

      setPreview(previewPayload);

      setAdjustments(
        adjustmentPayload?.adjustments || []
      );

      setBatchState(batchPayload);
    } catch (exc) {
      setError(
        exc?.message ||
        "Unable to load Payroll Hub."
      );
    } finally {
      setLoading(false);
    }
  }, [
    api,
    start,
    end,
    engagementType,
    state,
  ]);


  useEffect(() => {
    loadData();
  }, [loadData]);


  const adjustmentState =
    preview?.adjustment_state || {};


  const pendingAdjustments = useMemo(
    () =>
      adjustments.filter(
        (item) => item.status === "pending"
      ),
    [adjustments]
  );


  const historyAdjustments = useMemo(
    () =>
      adjustments.filter(
        (item) => item.status !== "pending"
      ),
    [adjustments]
  );


  function openCorrection(line) {
    if (!line?.attendance_id) {
      setError(
        "This payroll line has no attendance reference and cannot be adjusted."
      );

      return;
    }

    const field = "hours";

    setSelectedLine(line);

    setCreateForm({
      field,
      beforeValue: String(
        line[field] ?? 0
      ),
      afterValue: String(
        line[field] ?? 0
      ),
      reason: "",
    });
  }


  function changeCorrectionField(field) {
    if (!selectedLine) return;

    const value =
      selectedLine[field] ?? 0;

    setCreateForm((current) => ({
      ...current,
      field,
      beforeValue: String(value),
      afterValue: String(value),
    }));
  }


  async function submitCorrection(event) {
    event.preventDefault();

    if (!selectedLine) return;

    if (!createForm.reason.trim()) {
      setError(
        "A payroll adjustment reason is required."
      );

      return;
    }

    if (
      Number(createForm.beforeValue) ===
      Number(createForm.afterValue)
    ) {
      setError(
        "The corrected value must differ from the attendance-derived value."
      );

      return;
    }

    setWorking(true);
    setError("");

    try {
      await api.post(
        "/payroll/adjustments",
{
            attendance_id:
              selectedLine.attendance_id,

            payroll_line_id:
              selectedLine.attendance_id,

            worker_id:
              selectedLine.worker_id || "",

            worker_name:
              selectedLine.worker_name || null,

            site_id:
              selectedLine.site_id || null,

            period_start: start,
            period_end: end,

            field: createForm.field,

            before_value:
              Number(createForm.beforeValue),

            after_value:
              Number(createForm.afterValue),

            reason:
              createForm.reason.trim(),

            metadata: {
              source: "payroll_hub_ui",
              engagement_type:
                selectedLine.engagement_type,
            },
          }
      );

      setSelectedLine(null);

      await loadData();
    } catch (exc) {
      setError(
        exc?.message ||
        "Unable to create payroll adjustment."
      );
    } finally {
      setWorking(false);
    }
  }


  function openDecision(
    adjustment,
    action
  ) {
    setDecision({
      adjustment,
      action,
    });

    setDecisionReason("");
  }


  async function submitDecision(event) {
    event.preventDefault();

    if (!decision) return;

    const {
      adjustment,
      action,
    } = decision;

    if (
      ["reject", "void"].includes(action) &&
      !decisionReason.trim()
    ) {
      setError(
        `${statusLabel(action)} reason is required.`
      );

      return;
    }

    setWorking(true);
    setError("");

    try {
      await api.post(
        `/payroll/adjustments/${adjustment._id}/${action}`,
        {
          reason:
            decisionReason.trim() || null,
        }
      );

      setDecision(null);

      await loadData();
    } catch (exc) {
      setError(
        exc?.message ||
        `Unable to ${action} adjustment.`
      );
    } finally {
      setWorking(false);
    }
  }


  // ==========================================================
  // V4.2C-2 — FINALISE / REOPEN / EXPORT
  // ==========================================================

  function apiErrorMessage(error, fallback) {
    const detail =
      error?.response?.data?.detail;

    if (typeof detail === "string") {
      return detail;
    }

    if (detail?.message) {
      return detail.message;
    }

    return (
      error?.message ||
      fallback
    );
  }


  async function finalisePayroll(event) {
    event.preventDefault();

    if (!preview?.payroll_ready) {
      setError(
        "Payroll must be READY before finalisation."
      );
      return;
    }

    setBatchWorking(true);
    setError("");

    try {
      const query = new URLSearchParams({
        start,
        end,
        engagement_type: engagementType,
        state,
      });

      await api.post(
        `/payroll/batches/finalise?${query.toString()}`,
        {
          note:
            finaliseNote.trim() || null,
        }
      );

      setShowFinalise(false);
      setFinaliseNote("");

      await loadData();

    } catch (exc) {
      setError(
        apiErrorMessage(
          exc,
          "Unable to finalise payroll."
        )
      );
    } finally {
      setBatchWorking(false);
    }
  }


  async function reopenPayroll(event) {
    event.preventDefault();

    const batchId =
      batchState?.batch?._id;

    if (!batchId) {
      setError(
        "No payroll batch is available to reopen."
      );
      return;
    }

    if (!reopenReason.trim()) {
      setError(
        "A reopen reason is required."
      );
      return;
    }

    setBatchWorking(true);
    setError("");

    try {
      await api.post(
        `/payroll/batches/${batchId}/reopen`,
        {
          reason:
            reopenReason.trim(),
        }
      );

      setShowReopen(false);
      setReopenReason("");

      await loadData();

    } catch (exc) {
      setError(
        apiErrorMessage(
          exc,
          "Unable to reopen payroll."
        )
      );
    } finally {
      setBatchWorking(false);
    }
  }


  async function exportFinalisedPayroll() {
    setBatchWorking(true);
    setError("");

    try {
      const query = new URLSearchParams({
        start,
        end,
        engagement_type: engagementType,
        state,
      });

      const response = await api.post(
        `/payroll/unified/export?${query.toString()}`,
        {},
        {
          responseType: "blob",
        }
      );

      const blob = new Blob(
        [response.data],
        {
          type:
            response.headers?.[
              "content-type"
            ] ||
            "text/csv",
        }
      );

      const disposition =
        response.headers?.[
          "content-disposition"
        ] || "";

      const filenameMatch =
        disposition.match(
          /filename="?([^"]+)"?/i
        );

      const filename =
        filenameMatch?.[1] ||
        (
          "rapidx_unified_payroll_" +
          `${start}_to_${end}_${engagementType}.csv`
        );

      const url =
        window.URL.createObjectURL(
          blob
        );

      const link =
        document.createElement(
          "a"
        );

      link.href = url;
      link.download = filename;

      document.body.appendChild(
        link
      );

      link.click();
      link.remove();

      window.URL.revokeObjectURL(
        url
      );

      await loadData();

    } catch (exc) {
      setError(
        apiErrorMessage(
          exc,
          "Unable to export finalised payroll."
        )
      );
    } finally {
      setBatchWorking(false);
    }
  }


  const batch =
    batchState?.batch || null;

  const batchStatus =
    batchState?.status || "draft";

  const payrollLocked =
    batchState?.locked === true;

  const canExportBatch =
    batchState?.can_export === true;

  // ==========================================================
  // V4.2C-3 — REVISION HISTORY
  // ==========================================================

  function shortHash(value) {
    if (!value) {
      return "—";
    }

    const text = String(value);

    if (text.length <= 18) {
      return text;
    }

    return (
      text.slice(0, 10) +
      "…" +
      text.slice(-6)
    );
  }


  function formatDateTime(value) {
    if (!value) {
      return "—";
    }

    const date = new Date(value);

    if (
      Number.isNaN(
        date.getTime()
      )
    ) {
      return String(value);
    }

    return date.toLocaleString(
      "en-AU"
    );
  }


  async function loadRevisionHistory() {
    setRevisionHistoryLoading(true);
    setError("");

    try {
      const query = new URLSearchParams({
        start,
        end,
        engagement_type: engagementType,
        state,
      });

      const response = await api.get(
        `/payroll/batches/history?${query.toString()}`
      );

      setRevisionHistory(
        response.data?.history || []
      );

      setShowRevisionHistory(true);

    } catch (exc) {
      setError(
        apiErrorMessage(
          exc,
          "Unable to load payroll revision history."
        )
      );
    } finally {
      setRevisionHistoryLoading(false);
    }
  }


  async function closePayrollPeriod(event) {
    event.preventDefault();

    const batchId =
      batchState?.batch?._id;

    if (!batchId) {
      setError(
        "No exported payroll batch is available to close."
      );
      return;
    }

    setBatchWorking(true);
    setError("");

    try {
      await api.post(
        `/payroll/batches/${batchId}/close`,
        {
          note:
            closeNote.trim() || null,
        }
      );

      setShowClosePeriod(false);
      setCloseNote("");

      await loadData();

      if (showRevisionHistory) {
        await loadRevisionHistory();
      }

    } catch (exc) {
      setError(
        apiErrorMessage(
          exc,
          "Unable to close payroll period."
        )
      );

    } finally {
      setBatchWorking(false);
    }
  }


  const periodClosed =
    batchState?.period_closed === true ||
    batchState?.batch?.period_closed === true;

  const operationalStatus =
    periodClosed
      ? "closed"
      : (
          batchState?.operational_status ||
          batchState?.status ||
          "draft"
        );


  const lines =
    preview?.lines || [];


  const grandTotal =
    preview?.grand_total || {};


  return (
    <div className="rx-payroll-adjustments">

      <style>{`
        .rx-payroll-adjustments {
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .rx-pa-card {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          overflow: hidden;
        }

        .rx-pa-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          padding: 18px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .rx-pa-title {
          margin: 0;
          font-size: 18px;
          font-weight: 700;
          color: #111827;
        }

        .rx-pa-subtitle {
          margin: 5px 0 0;
          color: #6b7280;
          font-size: 13px;
        }

        .rx-pa-summary {
          display: grid;
          grid-template-columns:
            repeat(auto-fit, minmax(145px, 1fr));
          gap: 12px;
          padding: 16px 20px;
        }

        .rx-pa-stat {
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 12px;
        }

        .rx-pa-stat-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .05em;
          color: #6b7280;
          margin-bottom: 5px;
        }

        .rx-pa-stat-value {
          font-size: 20px;
          font-weight: 700;
          color: #111827;
        }

        .rx-pa-ready {
          color: #047857;
        }

        .rx-pa-blocked {
          color: #b91c1c;
        }

        .rx-table-wrap {
          overflow-x: auto;
        }

        .rx-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .rx-table th {
          padding: 10px 12px;
          text-align: left;
          background: #f9fafb;
          color: #4b5563;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: .04em;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
        }

        .rx-table td {
          padding: 12px;
          border-bottom: 1px solid #f0f1f3;
          vertical-align: top;
        }

        .rx-table tr:last-child td {
          border-bottom: none;
        }

        .rx-value-effective {
          font-weight: 700;
          color: #111827;
        }

        .rx-value-base {
          margin-top: 3px;
          color: #6b7280;
          font-size: 11px;
        }

        .rx-value-base.changed {
          text-decoration: line-through;
        }

        .rx-adjusted-cell {
          background: #fffbeb;
        }

        .rx-badge {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 4px 8px;
          font-size: 11px;
          font-weight: 700;
          background: #f3f4f6;
          color: #374151;
        }

        .rx-badge-approved {
          background: #ecfdf5;
          color: #047857;
        }

        .rx-badge-pending {
          background: #fff7ed;
          color: #c2410c;
        }

        .rx-badge-rejected {
          background: #fef2f2;
          color: #b91c1c;
        }

        .rx-badge-voided {
          background: #f3f4f6;
          color: #6b7280;
        }

        .rx-btn {
          appearance: none;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: #fff;
          color: #1f2937;
          padding: 7px 11px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
        }

        .rx-btn:hover {
          background: #f9fafb;
        }

        .rx-btn:disabled {
          opacity: .5;
          cursor: not-allowed;
        }

        .rx-btn-primary {
          background: #111827;
          color: #fff;
          border-color: #111827;
        }

        .rx-btn-danger {
          color: #b91c1c;
        }

        .rx-btn-success {
          color: #047857;
        }

        .rx-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }

        .rx-error {
          padding: 11px 14px;
          border-radius: 10px;
          border: 1px solid #fecaca;
          background: #fef2f2;
          color: #991b1b;
          font-size: 13px;
        }

        .rx-empty {
          padding: 24px;
          color: #6b7280;
          text-align: center;
        }

        .rx-modal-backdrop {
          position: fixed;
          inset: 0;
          z-index: 10000;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(17, 24, 39, .48);
          padding: 20px;
        }

        .rx-modal {
          width: min(560px, 100%);
          max-height: 90vh;
          overflow-y: auto;
          background: #fff;
          border-radius: 14px;
          box-shadow:
            0 20px 50px rgba(0, 0, 0, .25);
        }

        .rx-modal-head {
          padding: 18px 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .rx-modal-body {
          padding: 20px;
        }

        .rx-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        .rx-form-full {
          grid-column: 1 / -1;
        }

        .rx-field label {
          display: block;
          margin-bottom: 5px;
          color: #374151;
          font-size: 12px;
          font-weight: 700;
        }

        .rx-field input,
        .rx-field select,
        .rx-field textarea {
          width: 100%;
          box-sizing: border-box;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 9px 10px;
          font: inherit;
        }

        .rx-field textarea {
          min-height: 90px;
          resize: vertical;
        }

        .rx-modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          margin-top: 20px;
        }

        .rx-audit {
          margin: 6px 0 0;
          padding-left: 18px;
          font-size: 11px;
          color: #6b7280;
        }

        .rx-drawer-backdrop {
          position: fixed;
          inset: 0;
          z-index: 11000;
          background: rgba(17, 24, 39, .45);
          display: flex;
          justify-content: flex-end;
        }

        .rx-drawer {
          width: min(720px, 96vw);
          height: 100%;
          background: #fff;
          box-shadow:
            -15px 0 40px rgba(0, 0, 0, .22);
          display: flex;
          flex-direction: column;
        }

        .rx-drawer-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 16px;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .rx-drawer-body {
          padding: 18px;
          overflow-y: auto;
          flex: 1;
        }

        .rx-revision-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 12px;
        }

        .rx-revision-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
        }

        .rx-revision-title {
          font-size: 15px;
          font-weight: 700;
          color: #111827;
        }

        .rx-revision-grid {
          display: grid;
          grid-template-columns:
            repeat(2, minmax(0, 1fr));
          gap: 10px 18px;
          font-size: 12px;
        }

        .rx-revision-item {
          min-width: 0;
        }

        .rx-revision-label {
          color: #6b7280;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: .05em;
          margin-bottom: 3px;
        }

        .rx-revision-value {
          color: #111827;
          font-weight: 600;
          word-break: break-word;
        }

        .rx-revision-reason {
          margin-top: 12px;
          border-radius: 8px;
          background: #f9fafb;
          padding: 10px;
          color: #374151;
          font-size: 12px;
        }

        .rx-revision-hash {
          font-family: monospace;
          font-size: 11px;
        }


        @media (max-width: 700px) {
          .rx-form-grid {
            grid-template-columns: 1fr;
          }

          .rx-form-full {
            grid-column: auto;
          }
        }
      `}</style>


      {error && (
        <div className="rx-error">
          {error}
        </div>
      )}


      <section className="rx-pa-card">
        <div className="rx-pa-header">
          <div>
            <h2 className="rx-pa-title">
              Payroll Adjustments & Approval
            </h2>

            <p className="rx-pa-subtitle">
              Attendance remains the source of truth.
              Approved corrections alter payroll only.
            </p>
          </div>

          <button
            type="button"
            className="rx-btn"
            disabled={loading || working}
            onClick={loadData}
          >
            Refresh
          </button>
        </div>


        <div className="rx-pa-summary">
          <div className="rx-pa-stat">
            <div className="rx-pa-stat-label">
              Payroll status
            </div>

            <div
              className={`rx-pa-stat-value ${
                preview?.payroll_ready
                  ? "rx-pa-ready"
                  : "rx-pa-blocked"
              }`}
            >
              {preview?.payroll_ready
                ? "READY"
                : "BLOCKED"}
            </div>
          </div>

          <div className="rx-pa-stat">
            <div className="rx-pa-stat-label">
              Hours
            </div>

            <div className="rx-pa-stat-value">
              {number(grandTotal.hours)}
            </div>
          </div>

          <div className="rx-pa-stat">
            <div className="rx-pa-stat-label">
              Gross pay
            </div>

            <div className="rx-pa-stat-value">
              {money(grandTotal.pay)}
            </div>
          </div>

          <div className="rx-pa-stat">
            <div className="rx-pa-stat-label">
              Pending approval
            </div>

            <div className="rx-pa-stat-value">
              {Number(
                adjustmentState.pending_adjustments ||
                0
              )}
            </div>
          </div>

          <div className="rx-pa-stat">
            <div className="rx-pa-stat-label">
              Adjusted lines
            </div>

            <div className="rx-pa-stat-value">
              {Number(
                preview?.adjusted_line_count ||
                0
              )}
            </div>
          </div>
        </div>
      </section>


      <section className="rx-pa-card">
        <div className="rx-pa-header">
          <div>
            <h3 className="rx-pa-title">
              Finalisation & Export Control
            </h3>

            <p className="rx-pa-subtitle">
              Finalised payroll is locked to an immutable
              snapshot. Export uses that snapshot only.
            </p>
          </div>

          <span
            className={badgeClass(
              batchStatus
            )}
          >
            {statusLabel(
              batchStatus
            )}
          </span>
        </div>

        <div className="rx-pa-summary">
          <div className="rx-pa-stat">
            <div className="rx-pa-stat-label">
              Revision status
            </div>

            <div className="rx-pa-stat-value">
              {statusLabel(
                batchStatus
              )}
            </div>
          </div>

            <div className="rx-pa-stat">
              <div className="rx-pa-stat-label">
                Period status
              </div>

              <div className="rx-pa-stat-value">
                {periodClosed
                  ? "CLOSED"
                  : "OPEN"}
              </div>
            </div>

          <div className="rx-pa-stat">
            <div className="rx-pa-stat-label">
              Revision
            </div>

            <div className="rx-pa-stat-value">
              {batch?.revision || "—"}
            </div>
          </div>

          <div className="rx-pa-stat">
            <div className="rx-pa-stat-label">
              Period lock
            </div>

            <div className="rx-pa-stat-value">
              {payrollLocked
                ? "LOCKED"
                : "OPEN"}
            </div>
          </div>

          <div className="rx-pa-stat">
            <div className="rx-pa-stat-label">
              Export
            </div>

            <div className="rx-pa-stat-value">
              {canExportBatch
                ? "READY"
                : "BLOCKED"}
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "0 20px 18px",
          }}
        >
          {batch && (
            <div
              style={{
                display: "grid",
                gap: 6,
                marginBottom: 16,
                fontSize: 12,
                color: "#6b7280",
              }}
            >
              {batch.finalised_by_name && (
                <div>
                  Finalised by:{" "}
                  <strong>
                    {batch.finalised_by_name}
                  </strong>
                  {batch.finalised_at
                    ? ` — ${new Date(
                        batch.finalised_at
                      ).toLocaleString(
                        "en-AU"
                      )}`
                    : ""}
                </div>
              )}

              {batch.exported_by_name && (
                <div>
                  Exported by:{" "}
                  <strong>
                    {batch.exported_by_name}
                  </strong>
                  {batch.exported_at
                    ? ` — ${new Date(
                        batch.exported_at
                      ).toLocaleString(
                        "en-AU"
                      )}`
                    : ""}
                </div>
              )}

                {periodClosed && (
                  <>
                    <div>
                      Closed by:{" "}
                      <strong>
                        {batch.closed_by_name ||
                          batch.closed_by ||
                          "Unknown"}
                      </strong>

                      {batch.closed_at
                        ? ` — ${new Date(
                            batch.closed_at
                          ).toLocaleString(
                            "en-AU"
                          )}`
                        : ""}
                    </div>

                    {batch.close_note && (
                      <div>
                        Close note:{" "}
                        <strong>
                          {batch.close_note}
                        </strong>
                      </div>
                    )}
                  </>
                )}

              {batch.snapshot_hash && (
                <div>
                  Snapshot:{" "}
                  <code>
                    {batch.snapshot_hash}
                  </code>
                </div>
              )}
            </div>
          )}

            {periodClosed && (
              <div
                style={{
                  marginBottom: 14,
                  padding: "12px 14px",
                  borderRadius: 8,
                  border:
                    "1px solid rgba(59, 130, 246, 0.45)",
                  background:
                    "rgba(59, 130, 246, 0.08)",
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                <strong>
                  Payroll period operationally CLOSED.
                </strong>
                {" "}
                Revision {batch?.revision} remains permanently
                exported and locked. Further payroll changes
                require an explicit reopen into a new revision.
              </div>
            )}

          <div className="rx-actions">


            <button
              type="button"
              className="rx-btn"
              disabled={revisionHistoryLoading}
              onClick={loadRevisionHistory}
            >
              {revisionHistoryLoading
                ? "Loading History..."
                : "View Revision History"}
            </button>

            {(
              batchStatus === "draft" ||
              batchStatus === "reopened"
            ) && (
              <button
                type="button"
                className="rx-btn rx-btn-primary"
                disabled={
                  batchWorking ||
                  preview?.payroll_ready !== true
                }
                onClick={() =>
                  setShowFinalise(true)
                }
              >
                Finalise Payroll
              </button>
            )}

            {batchStatus ===
              "finalised" &&
              canExportBatch && (
                <button
                  type="button"
                  className="rx-btn rx-btn-primary"
                  disabled={batchWorking}
                  onClick={
                    exportFinalisedPayroll
                  }
                >
                  Export Locked CSV
                </button>
              )}

            {batchStatus === "exported" &&
              !periodClosed && (
                <button
                  type="button"
                  className="rx-btn rx-btn-primary"
                  disabled={batchWorking}
                  onClick={() =>
                    setShowClosePeriod(true)
                  }
                >
                  Close Payroll Period
                </button>
              )}

            {(
              batchStatus === "finalised" ||
              batchStatus === "exported"
            ) && (
              <button
                type="button"
                className="rx-btn rx-btn-danger"
                disabled={batchWorking}
                onClick={() =>
                  setShowReopen(true)
                }
              >
                Reopen Payroll
              </button>
            )}

          </div>

          {!preview?.payroll_ready &&
            (
              batchStatus === "draft" ||
              batchStatus === "reopened"
            ) && (
              <div
                className="rx-error"
                style={{
                  marginTop: 12,
                }}
              >
                Resolve payroll exceptions and
                pending adjustments before
                finalising this period.
              </div>
            )}
        </div>
      </section>


      <section className="rx-pa-card">
        <div className="rx-pa-header">
          <div>
            <h3 className="rx-pa-title">
              Payroll Lines
            </h3>

            <p className="rx-pa-subtitle">
              Attendance-derived values remain visible
              beneath approved effective values.
            </p>
          </div>
        </div>

        <div className="rx-table-wrap">
          <table className="rx-table">
            <thead>
              <tr>
                <th>Worker</th>
                <th>Engagement</th>
                <th>Date</th>
                <th>Hours</th>
                <th>Rate</th>
                <th>Pay</th>
                <th>Source</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {lines.map((line, index) => {
                const base =
                  line._base || line;

                const hoursChanged =
                  !sameValue(
                    line.hours,
                    base.hours
                  );

                const rateChanged =
                  !sameValue(
                    line.rate,
                    base.rate
                  );

                const amountChanged =
                  !sameValue(
                    line.amount,
                    base.amount
                  );

                return (
                  <tr
                    key={
                      line.attendance_id ||
                      `${line.worker_id}-${index}`
                    }
                  >
                    <td>
                      <strong>
                        {line.worker_name ||
                          "Unknown Worker"}
                      </strong>

                      <div className="rx-value-base">
                        {line.site_name || ""}
                      </div>
                    </td>

                    <td>
                      {line.engagement_type}
                    </td>

                    <td>
                      {line.date ||
                        line.date_iso ||
                        "—"}
                    </td>

                    <td
                      className={
                        hoursChanged
                          ? "rx-adjusted-cell"
                          : ""
                      }
                    >
                      <div className="rx-value-effective">
                        {number(line.hours)}
                      </div>

                      {hoursChanged && (
                        <div className="rx-value-base changed">
                          Attendance:{" "}
                          {number(base.hours)}
                        </div>
                      )}
                    </td>

                    <td
                      className={
                        rateChanged
                          ? "rx-adjusted-cell"
                          : ""
                      }
                    >
                      <div className="rx-value-effective">
                        {money(line.rate)}
                      </div>

                      {rateChanged && (
                        <div className="rx-value-base changed">
                          Base: {money(base.rate)}
                        </div>
                      )}
                    </td>

                    <td
                      className={
                        amountChanged
                          ? "rx-adjusted-cell"
                          : ""
                      }
                    >
                      <div className="rx-value-effective">
                        {money(line.amount)}
                      </div>

                      {amountChanged && (
                        <div className="rx-value-base changed">
                          Attendance-derived:{" "}
                          {money(base.amount)}
                        </div>
                      )}
                    </td>

                    <td>
                      {line.adjusted ? (
                        <span className="rx-badge rx-badge-approved">
                          Adjusted
                        </span>
                      ) : (
                        <span className="rx-badge">
                          Attendance-derived
                        </span>
                      )}
                    </td>

                    <td>
                      {canCreate &&
                        !payrollLocked &&
                        line.attendance_id && (
                          <button
                            type="button"
                            className="rx-btn"
                            onClick={() =>
                              openCorrection(line)
                            }
                          >
                            Raise correction
                          </button>
                        )}
                    </td>
                  </tr>
                );
              })}

              {!loading &&
                lines.length === 0 && (
                  <tr>
                    <td
                      colSpan="8"
                      className="rx-empty"
                    >
                      No payroll lines for this period.
                    </td>
                  </tr>
                )}
            </tbody>
          </table>
        </div>
      </section>


      <section className="rx-pa-card">
        <div className="rx-pa-header">
          <div>
            <h3 className="rx-pa-title">
              Pending Approval
            </h3>

            <p className="rx-pa-subtitle">
              Pending corrections block payroll export
              until resolved.
            </p>
          </div>

          <span className="rx-badge rx-badge-pending">
            {pendingAdjustments.length} pending
          </span>
        </div>

        <div className="rx-table-wrap">
          <table className="rx-table">
            <thead>
              <tr>
                <th>Worker</th>
                <th>Field</th>
                <th>Before</th>
                <th>After</th>
                <th>Reason</th>
                <th>Raised by</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {pendingAdjustments.map(
                (item) => {
                  const isMaker =
                    actorId(currentUser) &&
                    actorId(currentUser) ===
                      String(item.created_by);

                  return (
                    <tr key={item._id}>
                      <td>
                        {item.worker_name ||
                          item.worker_id}
                      </td>

                      <td>
                        <span className="rx-badge rx-badge-pending">
                          {fieldLabel(item.field)}
                        </span>
                      </td>

                      <td>
                        {item.field === "amount" ||
                        item.field === "rate"
                          ? money(
                              item.before_value
                            )
                          : number(
                              item.before_value
                            )}
                      </td>

                      <td>
                        <strong>
                          {item.field === "amount" ||
                          item.field === "rate"
                            ? money(
                                item.after_value
                              )
                            : number(
                                item.after_value
                              )}
                        </strong>
                      </td>

                      <td>{item.reason}</td>

                      <td>
                        {item.created_by_name ||
                          item.created_by}
                      </td>

                      <td>
                        {item.created_at
                          ? new Date(
                              item.created_at
                            ).toLocaleString(
                              "en-AU"
                            )
                          : "—"}
                      </td>

                      <td>
                        <div className="rx-actions">
                          {canApprove &&
                            !isMaker && (
                              <button
                                type="button"
                                className="rx-btn rx-btn-success"
                                onClick={() =>
                                  openDecision(
                                    item,
                                    "approve"
                                  )
                                }
                              >
                                Approve
                              </button>
                            )}

                          {canApprove && (
                            <button
                              type="button"
                              className="rx-btn rx-btn-danger"
                              onClick={() =>
                                openDecision(
                                  item,
                                  "reject"
                                )
                              }
                            >
                              Reject
                            </button>
                          )}

                          {isMaker && (
                            <span className="rx-badge">
                              Maker — cannot approve
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }
              )}

              {pendingAdjustments.length === 0 && (
                <tr>
                  <td
                    colSpan="8"
                    className="rx-empty"
                  >
                    No payroll adjustments awaiting
                    approval.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>


      <section className="rx-pa-card">
        <div className="rx-pa-header">
          <div>
            <h3 className="rx-pa-title">
              Adjustment History
            </h3>

            <p className="rx-pa-subtitle">
              Approved, rejected and voided payroll
              decisions remain available for audit.
            </p>
          </div>
        </div>

        <div className="rx-table-wrap">
          <table className="rx-table">
            <thead>
              <tr>
                <th>Worker</th>
                <th>Status</th>
                <th>Change</th>
                <th>Reason</th>
                <th>Audit trail</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {historyAdjustments.map(
                (item) => (
                  <tr key={item._id}>
                    <td>
                      {item.worker_name ||
                        item.worker_id}
                    </td>

                    <td>
                      <span
                        className={badgeClass(
                          item.status
                        )}
                      >
                        {statusLabel(
                          item.status
                        )}
                      </span>
                    </td>

                    <td>
                      <strong>
                        {fieldLabel(item.field)}
                      </strong>

                      <div className="rx-value-base">
                        {String(
                          item.before_value
                        )}
                        {" → "}
                        {String(
                          item.after_value
                        )}
                      </div>
                    </td>

                    <td>{item.reason}</td>

                    <td>
                      <ul className="rx-audit">
                        {(
                          item.audit_history ||
                          []
                        ).map(
                          (event, index) => (
                            <li
                              key={`${item._id}-${index}`}
                            >
                              {statusLabel(
                                event.action
                              )}
                              {" — "}
                              {event.actor_name ||
                                event.actor_id}
                              {event.timestamp
                                ? ` — ${new Date(
                                    event.timestamp
                                  ).toLocaleString(
                                    "en-AU"
                                  )}`
                                : ""}
                            </li>
                          )
                        )}
                      </ul>
                    </td>

                    <td>
                      {item.status ===
                        "approved" &&
                        canApprove && (
                          <button
                            type="button"
                            className="rx-btn rx-btn-danger"
                            onClick={() =>
                              openDecision(
                                item,
                                "void"
                              )
                            }
                          >
                            Void
                          </button>
                        )}
                    </td>
                  </tr>
                )
              )}

              {historyAdjustments.length === 0 && (
                <tr>
                  <td
                    colSpan="6"
                    className="rx-empty"
                  >
                    No completed adjustment history for
                    this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>


      {showRevisionHistory && (
        <div
          className="rx-drawer-backdrop"
          onClick={() =>
            setShowRevisionHistory(false)
          }
        >
          <aside
            className="rx-drawer"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="rx-drawer-head">
              <div>
                <h3 className="rx-pa-title">
                  Payroll Revision History
                </h3>

                <p className="rx-pa-subtitle">
                  {start} to {end}
                  {" — "}
                  {engagementType}
                  {" — "}
                  {state}
                </p>
              </div>

              <button
                type="button"
                className="rx-btn"
                onClick={() =>
                  setShowRevisionHistory(false)
                }
              >
                Close
              </button>
            </div>

            <div className="rx-drawer-body">

              {revisionHistory.length === 0 && (
                <div className="rx-empty">
                  No payroll revisions exist
                  for this period.
                </div>
              )}

              {[...revisionHistory]
                .reverse()
                .map((item) => (
                  <div
                    className="rx-revision-card"
                    key={
                      item._id ||
                      item.revision
                    }
                  >
                    <div className="rx-revision-head">
                      <div className="rx-revision-title">
                        Revision{" "}
                        {item.revision}
                      </div>

                      <span
                        className={badgeClass(
                          item.status
                        )}
                      >
                        {statusLabel(
                          item.status
                        )}
                      </span>
                    </div>

                    <div className="rx-revision-grid">

                      <div className="rx-revision-item">
                        <div className="rx-revision-label">
                          Total Pay
                        </div>

                        <div className="rx-revision-value">
                          {item.pay !== null &&
                          item.pay !== undefined
                            ? money(item.pay)
                            : "—"}
                        </div>
                      </div>

                      <div className="rx-revision-item">
                        <div className="rx-revision-label">
                          Hours
                        </div>

                        <div className="rx-revision-value">
                          {item.hours !== null &&
                          item.hours !== undefined
                            ? number(item.hours)
                            : "—"}
                        </div>
                      </div>

                      <div className="rx-revision-item">
                        <div className="rx-revision-label">
                          Source Revision
                        </div>

                        <div className="rx-revision-value">
                          {item.reopened_from_revision
                            ? `Revision ${item.reopened_from_revision}`
                            : "Original"}
                        </div>
                      </div>

                      <div className="rx-revision-item">
                        <div className="rx-revision-label">
                          Lock
                        </div>

                        <div className="rx-revision-value">
                          {item.locked
                            ? "LOCKED"
                            : "OPEN"}
                        </div>
                      </div>

                      <div className="rx-revision-item">
                        <div className="rx-revision-label">
                          Reopened By
                        </div>

                        <div className="rx-revision-value">
                          {item.reopened_by_name ||
                            item.reopened_by ||
                            "—"}
                        </div>

                        <div className="rx-value-base">
                          {formatDateTime(
                            item.reopened_at
                          )}
                        </div>
                      </div>

                      <div className="rx-revision-item">
                        <div className="rx-revision-label">
                          Finalised By
                        </div>

                        <div className="rx-revision-value">
                          {item.finalised_by_name ||
                            item.finalised_by ||
                            "—"}
                        </div>

                        <div className="rx-value-base">
                          {formatDateTime(
                            item.finalised_at
                          )}
                        </div>
                      </div>

                      <div className="rx-revision-item">
                        <div className="rx-revision-label">
                          Exported By
                        </div>

                        <div className="rx-revision-value">
                          {item.exported_by_name ||
                            item.exported_by ||
                            "—"}
                        </div>

                        <div className="rx-value-base">
                          {formatDateTime(
                            item.exported_at
                          )}
                        </div>
                      </div>

                      <div className="rx-revision-item">
                        <div className="rx-revision-label">
                          Closed By
                        </div>

                        <div className="rx-revision-value">
                          {item.closed_by_name ||
                            item.closed_by ||
                            "—"}
                        </div>

                        <div className="rx-value-base">
                          {formatDateTime(
                            item.closed_at
                          )}
                        </div>
                      </div>

                      <div className="rx-revision-item">
                        <div className="rx-revision-label">
                          Snapshot
                        </div>

                        <div
                          className="
                            rx-revision-value
                            rx-revision-hash
                          "
                          title={
                            item.snapshot_hash ||
                            item.source_snapshot_hash ||
                            ""
                          }
                        >
                          {shortHash(
                            item.snapshot_hash ||
                            item.source_snapshot_hash
                          )}
                        </div>
                      </div>

                    </div>

                    {item.reopen_reason && (
                      <div className="rx-revision-reason">
                        <strong>
                          Reopen reason:
                        </strong>
                        {" "}
                        {item.reopen_reason}
                      </div>
                    )}

                    {item.close_note && (
                      <div className="rx-revision-reason">
                        <strong>
                          Close note:
                        </strong>
                        {" "}
                        {item.close_note}
                      </div>
                    )}

                    {item.export_reference && (
                      <div
                        className="rx-value-base"
                        style={{
                          marginTop: 10,
                        }}
                      >
                        Export:{" "}
                        {item.export_reference}
                      </div>
                    )}

                  </div>
                ))}

            </div>
          </aside>
        </div>
      )}


      {showFinalise && (
        <div className="rx-modal-backdrop">
          <div className="rx-modal">

            <div className="rx-modal-head">
              <h3 className="rx-pa-title">
                Finalise Payroll
              </h3>

              <p className="rx-pa-subtitle">
                This will lock the current effective
                payroll into an immutable snapshot.
              </p>
            </div>

            <form
              className="rx-modal-body"
              onSubmit={finalisePayroll}
            >
              <div className="rx-field">
                <label>
                  Approval note
                </label>

                <textarea
                  value={finaliseNote}
                  onChange={(event) =>
                    setFinaliseNote(
                      event.target.value
                    )
                  }
                  placeholder="Optional finalisation note."
                />
              </div>

              <div className="rx-modal-actions">
                <button
                  type="button"
                  className="rx-btn"
                  disabled={batchWorking}
                  onClick={() =>
                    setShowFinalise(false)
                  }
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="rx-btn rx-btn-primary"
                  disabled={batchWorking}
                >
                  {batchWorking
                    ? "Finalising..."
                    : "Finalise & Lock"}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}


      {showClosePeriod && (
        <div className="rx-modal-backdrop">
          <div className="rx-modal">

            <div className="rx-modal-head">
              <h3 className="rx-pa-title">
                Close Payroll Period
              </h3>

              <p className="rx-pa-subtitle">
                Closing confirms that this exported payroll
                period is operationally complete.
                Further payroll changes require an explicit
                reopen into a new revision.
              </p>
            </div>

            <form
              className="rx-modal-body"
              onSubmit={closePayrollPeriod}
            >
              <div className="rx-field">
                <label>
                  Close note
                </label>

                <textarea
                  value={closeNote}
                  onChange={(event) =>
                    setCloseNote(
                      event.target.value
                    )
                  }
                  placeholder="Optional payroll close note."
                />
              </div>

              <div className="rx-modal-actions">
                <button
                  type="button"
                  className="rx-btn"
                  disabled={batchWorking}
                  onClick={() =>
                    setShowClosePeriod(false)
                  }
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="rx-btn rx-btn-primary"
                  disabled={batchWorking}
                >
                  {batchWorking
                    ? "Closing..."
                    : "Confirm Period Close"}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}


      {showReopen && (
        <div className="rx-modal-backdrop">
          <div className="rx-modal">

            <div className="rx-modal-head">
              <h3 className="rx-pa-title">
                Reopen Payroll
              </h3>

              <p className="rx-pa-subtitle">
                Reopening removes the period lock and
                blocks export until payroll is
                finalised again.
              </p>
            </div>

            <form
              className="rx-modal-body"
              onSubmit={reopenPayroll}
            >
              <div className="rx-field">
                <label>
                  Reopen reason
                </label>

                <textarea
                  value={reopenReason}
                  onChange={(event) =>
                    setReopenReason(
                      event.target.value
                    )
                  }
                  placeholder="Reason is required."
                  required
                />
              </div>

              <div className="rx-modal-actions">
                <button
                  type="button"
                  className="rx-btn"
                  disabled={batchWorking}
                  onClick={() =>
                    setShowReopen(false)
                  }
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="rx-btn rx-btn-danger"
                  disabled={batchWorking}
                >
                  {batchWorking
                    ? "Reopening..."
                    : "Reopen Payroll"}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}


      {selectedLine && (
        <div className="rx-modal-backdrop">
          <div className="rx-modal">
            <div className="rx-modal-head">
              <h3 className="rx-pa-title">
                Raise Payroll Correction
              </h3>

              <p className="rx-pa-subtitle">
                {selectedLine.worker_name}
                {" — "}
                {selectedLine.date ||
                  selectedLine.date_iso}
              </p>
            </div>

            <form
              onSubmit={submitCorrection}
              className="rx-modal-body"
            >
              <div className="rx-form-grid">
                <div className="rx-field rx-form-full">
                  <label>
                    Correction field
                  </label>

                  <select
                    value={createForm.field}
                    onChange={(event) =>
                      changeCorrectionField(
                        event.target.value
                      )
                    }
                  >
                    <option value="hours">
                      Hours
                    </option>

                    <option value="rate">
                      Hourly Rate
                    </option>

                    <option value="amount">
                      Pay Amount
                    </option>
                  </select>
                </div>

                <div className="rx-field">
                  <label>
                    Attendance-derived value
                  </label>

                  <input
                    value={
                      createForm.beforeValue
                    }
                    disabled
                  />
                </div>

                <div className="rx-field">
                  <label>
                    Corrected payroll value
                  </label>

                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={
                      createForm.afterValue
                    }
                    onChange={(event) =>
                      setCreateForm(
                        (current) => ({
                          ...current,
                          afterValue:
                            event.target.value,
                        })
                      )
                    }
                    required
                  />
                </div>

                <div className="rx-field rx-form-full">
                  <label>
                    Reason for correction
                  </label>

                  <textarea
                    value={
                      createForm.reason
                    }
                    onChange={(event) =>
                      setCreateForm(
                        (current) => ({
                          ...current,
                          reason:
                            event.target.value,
                        })
                      )
                    }
                    placeholder="Explain why payroll should differ from the recorded attendance-derived value."
                    required
                  />
                </div>
              </div>

              <div className="rx-modal-actions">
                <button
                  type="button"
                  className="rx-btn"
                  onClick={() =>
                    setSelectedLine(null)
                  }
                  disabled={working}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="rx-btn rx-btn-primary"
                  disabled={working}
                >
                  {working
                    ? "Submitting..."
                    : "Submit for approval"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {decision && (
        <div className="rx-modal-backdrop">
          <div className="rx-modal">
            <div className="rx-modal-head">
              <h3 className="rx-pa-title">
                {statusLabel(
                  decision.action
                )}{" "}
                Payroll Adjustment
              </h3>

              <p className="rx-pa-subtitle">
                {decision.adjustment.worker_name ||
                  decision.adjustment.worker_id}
                {" — "}
                {fieldLabel(
                  decision.adjustment.field
                )}
              </p>
            </div>

            <form
              onSubmit={submitDecision}
              className="rx-modal-body"
            >
              <div className="rx-field">
                <label>
                  {decision.action === "approve"
                    ? "Approval note"
                    : `${statusLabel(
                        decision.action
                      )} reason`}
                </label>

                <textarea
                  value={decisionReason}
                  onChange={(event) =>
                    setDecisionReason(
                      event.target.value
                    )
                  }
                  required={
                    decision.action !==
                    "approve"
                  }
                  placeholder={
                    decision.action ===
                    "approve"
                      ? "Optional approval note."
                      : "Reason is required."
                  }
                />
              </div>

              <div className="rx-modal-actions">
                <button
                  type="button"
                  className="rx-btn"
                  disabled={working}
                  onClick={() =>
                    setDecision(null)
                  }
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className={
                    decision.action ===
                    "approve"
                      ? "rx-btn rx-btn-primary"
                      : "rx-btn rx-btn-danger"
                  }
                  disabled={working}
                >
                  {working
                    ? "Processing..."
                    : statusLabel(
                        decision.action
                      )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
